import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Copy, Check, ShieldCheck, AlertTriangle } from "lucide-react";
import { copyText, api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const fmtSol = (n) => (n == null ? "—" : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 })} SOL`);
export const fmtNum = (n, d = 2) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }) : "—");
export const shortMint = (m) => (m ? `${m.slice(0, 4)}…${m.slice(-4)}` : "");

const CHIP = {
  upcoming: { label: "Upcoming", cls: "border-aura-cyan/40 text-aura-cyan" },
  active: { label: "Live", cls: "border-aura-mint/40 text-aura-mint" },
  ended: { label: "Ended", cls: "border-am-border-strong text-am-text2" },
  unavailable: { label: "Status unavailable", cls: "border-aura-gold/40 text-aura-gold" },
};

export function StatusChip({ status, className }) {
  const c = CHIP[status] || CHIP.unavailable;
  return (
    <span data-testid="genesis-status-chip" data-status={status || "loading"} className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", c.cls, className)}>
      {status === "active" && <span className="h-1.5 w-1.5 rounded-full bg-aura-mint animate-pulse" />}
      {status ? c.label : "…"}
    </span>
  );
}

export function Countdown({ countdown, status, compact = false }) {
  if (!countdown || !status || status === "ended" || status === "unavailable") return null;
  const parts = [["d", countdown.d], ["h", countdown.h], ["m", countdown.m], ["s", countdown.s]];
  return (
    <div data-testid="genesis-countdown" className={cn("flex items-baseline gap-2", compact ? "text-sm" : "text-base")}>
      <span className="text-xs text-am-text2">{status === "upcoming" ? "Starts in" : "Ends in"}</span>
      <span className="font-mono font-bold tabular-nums text-white">{parts.map(([u, v]) => `${String(v).padStart(2, "0")}${u}`).join(" ")}</span>
    </div>
  );
}

export function ProgressBar({ progress }) {
  if (!progress) return null;
  return (
    <div data-testid="genesis-progress">
      <div className="flex items-center justify-between text-xs text-am-text2"><span>{fmtSol(progress.sold_sol)} raised</span><span>{fmtNum(progress.percent, 1)}% of {fmtSol(progress.hard_cap_sol)}</span></div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full aura-gradient transition-[width] duration-700" style={{ width: `${Math.min(100, progress.percent)}%` }} /></div>
    </div>
  );
}

export function VerifiedTag({ data, className }) {
  if (!data) return null;
  const mins = data.updated_at ? Math.max(0, Math.round((Date.now() - new Date(data.updated_at).getTime()) / 60000)) : null;
  return data.verified ? (
    <span data-testid="genesis-verified-tag" className={cn("inline-flex items-center gap-1 text-[11px] font-semibold text-aura-mint", className)}><ShieldCheck className="h-3.5 w-3.5" /> Verified from {data.provider.name} · updated {mins === 0 ? "just now" : `${mins} min ago`}</span>
  ) : (
    <span data-testid="genesis-unverified-tag" className={cn("inline-flex items-center gap-1 text-[11px] font-semibold text-aura-gold", className)}><AlertTriangle className="h-3.5 w-3.5" /> Unverified — confirm on the official page</span>
  );
}

export function MintCopy({ mint, full = false }) {
  const [ok, setOk] = useState(false);
  const copy = async () => { if (await copyText(mint)) { setOk(true); setTimeout(() => setOk(false), 1600); } };
  return (
    <button onClick={copy} data-testid="genesis-mint-copy" className="group inline-flex max-w-full items-center gap-2 rounded-full border border-am-border bg-am-surface2 px-3 py-1.5 font-mono text-xs text-am-text2 hover:border-am-border-strong">
      <span className={cn("truncate", full && "hidden sm:inline")}>{full ? mint : shortMint(mint)}</span>
      {full && <span className="sm:hidden">{shortMint(mint)}</span>}
      {ok ? <Check className="h-3.5 w-3.5 text-aura-mint" /> : <Copy className="h-3.5 w-3.5 text-am-text3 group-hover:text-white" />}
    </button>
  );
}

export function ExploreButton({ className, testid = "genesis-explore", secondary = false }) {
  return <Link to="/genesis" data-testid={testid} className={cn("inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-transform hover:scale-[1.03]", secondary ? "border border-am-border-strong text-white hover:bg-white/5" : "bg-white text-black", className)}>Explore Genesis <ArrowRight className="h-4 w-4" /></Link>;
}

const BUY_SIZE = { sm: "px-4 py-2 text-sm", md: "px-6 py-3 text-base", lg: "px-8 py-4 text-lg" };

// Primary presale CTA. Admin kill-switch (purchase_link_enabled) hides it; never shown once the sale has ended.
// Always a new tab on the official provider, never an iframe.
export function BuyButton({ data, status, size = "md", className, testid = "genesis-buy", surface = "page" }) {
  if (!data?.purchase_link_enabled || status === "ended") return null;
  const track = () => { api.genesisTrack({ action: "buy_click", surface }).catch(() => {}); };
  return (
    <a href={data.provider.url} target="_blank" rel="noopener noreferrer" onClick={track} data-testid={testid} data-surface={surface} className={cn("genesis-cta inline-flex items-center justify-center gap-2 rounded-full aura-gradient font-extrabold text-black transition-transform hover:scale-[1.04] active:scale-[0.98]", BUY_SIZE[size], className)}>
      Buy on {data.provider.name} <ExternalLink className={size === "lg" ? "h-5 w-5" : "h-4 w-4"} />
    </a>
  );
}

export function RiskNote({ className }) {
  return <p className={cn("text-[11px] leading-relaxed text-am-text3", className)} data-testid="genesis-risk-note">Purchases happen on the official provider, not in AuraMax. Risk of total loss · not financial advice · eligibility depends on your jurisdiction.</p>;
}

// Secondary text-style provider link (used where the big Buy button is already present).
export function OfficialLink({ data, className, testid = "genesis-official-link" }) {
  if (!data?.purchase_link_enabled) return null;
  return <a href={data.provider.url} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cn("inline-flex items-center justify-center gap-1.5 rounded-full border border-am-border-strong px-4 py-2 text-sm font-semibold text-white hover:bg-white/5", className)}>Open official {data.provider.name} presale <ExternalLink className="h-4 w-4" /></a>;
}
